import React, { useState, useCallback, useEffect } from "react";
import "../super-admin/customers/ControlPanel.css";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../components/Component";
import { Link, useParams, useLocation } from "react-router-dom";
import { Tabs, Tag, Alert } from "antd";
import LoadingSpinner from "../../components/loading/LoadingSpinner";
import {
  InfoCircleOutlined,
  ControlOutlined,
  AppstoreOutlined,
  TeamOutlined,
  TagOutlined,
  CalculatorOutlined,
  SafetyOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import PartyDetails from "./PartyDetails";
import CustomerControlPanel from "../super-admin/customers/CustomerControlPanel";
import superAdminService from "../../api/super-admin/superAdminService";

const PartyDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const orgID = Number(id);
  const [orgData, setOrgData] = useState<any>(null);
  const [partyId, setPartyId] = useState<number | null>(null);
  const [settingUp, setSettingUp] = useState(false);
  const [billingStatus, setBillingStatus] = useState<string | null>(null);
  const requestedTab = (location.state as any)?.activeTab || "details";
  const [activeTab, setActiveTab] = useState("details");

  const handleDataLoaded = useCallback((data: any) => {
    console.log("[PartyDetailsPage] org data loaded:", { orgID, partyId: data?.partyId, name: data?.name });
    setOrgData(data);
    if (data?.partyId) {
      setPartyId(data.partyId);
    }
  }, []);

  // Fetch billing status when partyId is available
  useEffect(() => {
    if (!partyId) return;
    const fetchBillingStatus = async () => {
      try {
        const res = await superAdminService.getCustomerById(partyId);
        if (res.status && res.data) {
          setBillingStatus(res.data.billingStatus || null);
        }
      } catch (err) {
        console.error("[PartyDetailsPage] Failed to fetch billing status:", err);
      }
    };
    fetchBillingStatus();
  }, [partyId]);

  const handleActivateCustomer = async () => {
    const result = await Swal.fire({
      title: "Activate Customer?",
      text: "This will change the billing status from Draft to Active. The customer will gain access to assigned modules.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Activate",
      confirmButtonColor: "#52c41a",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.activateCustomer(partyId!);
      if (res.status) {
        Swal.fire("Activated!", res.message || "Customer activated successfully.", "success");
        setBillingStatus("Active");
      } else {
        Swal.fire("Cannot Activate", res.message || "Activation failed.", "warning");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message || "Activation failed", "error");
    }
  };

  // When partyId becomes available and user wanted control-panel tab, switch to it
  useEffect(() => {
    if (partyId && requestedTab === "control-panel") {
      setActiveTab("control-panel");
    }
  }, [partyId, requestedTab]);

  const handleSetupParty = async () => {
    const result = await Swal.fire({
      title: "Setup Control Panel?",
      text: "This will create a party record and billing configuration for this organization.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Setup",
      confirmButtonColor: "#6576ff",
    });

    if (!result.isConfirmed) return;

    setSettingUp(true);
    try {
      console.log("[PartyDetailsPage] Setting up party for orgID:", orgID);
      const res = await superAdminService.setupPartyForOrg(orgID);
      console.log("[PartyDetailsPage] Setup response:", res);
      if (res.status) {
        const newPartyId = res.data?.partyId;
        console.log("[PartyDetailsPage] New partyId:", newPartyId);
        if (newPartyId) {
          setPartyId(newPartyId);
        }
        Swal.fire("Success", res.message || "Control panel setup successfully", "success");
      } else {
        console.warn("[PartyDetailsPage] Setup failed:", res.message);
        Swal.fire("Error", res.message || "Failed to setup", "error");
      }
    } catch (error: any) {
      console.error("[PartyDetailsPage] Setup error:", error);
      Swal.fire("Error", error.message || "Failed to setup", "error");
    } finally {
      setSettingUp(false);
    }
  };

  const tabItems = [
    {
      key: "details",
      label: (
        <span><InfoCircleOutlined /> Organisation Details</span>
      ),
      children: (
        <PartyDetails orgID={orgID} embedded onDataLoaded={handleDataLoaded} />
      ),
    },
    ...(partyId
      ? [
          {
            key: "control-panel",
            label: (
              <span ><ControlOutlined /> Control Panel</span>
            ),
            children: (
              <CustomerControlPanel partyId={partyId} embedded />
            ),
          },
        ]
      : orgData
        ? [
            {
              key: "control-panel",
              label: (
                <span><ControlOutlined /> Control Panel</span>
              ),
              children: (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 40 }}>
                  <Alert
                    type="warning"
                    showIcon
                    icon={<SettingOutlined />}
                    message={<strong>Control Panel not set up</strong>}
                    description="This organization was created before the billing system. Set up the control panel to manage billing, modules, and subscriptions."
                    style={{ maxWidth: 600, marginBottom: 24 }}
                  />
                  <Button
                    color="primary"
                    size="lg"
                    onClick={handleSetupParty}
                    disabled={settingUp}
                  >
                    {settingUp ? (
                      <><LoadingSpinner size="sm" inline /> Setting up...</>
                    ) : (
                      <><Icon name="setting" /> Setup Control Panel</>
                    )}
                  </Button>
                </div>
              ),
            },
          ]
        : []),
  ];

  return (
    <React.Fragment>
      <Head title={orgData?.name ? `${orgData.name} - Details` : "Party Details"} />
      <Content>
        <BlockHead size="sm" className="pb-0">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                <span className="cp-header-title" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {orgData?.name || "Party Details"}
                  {orgData?.status && (
                    <Tag
                      color={orgData.status === "Active" ? "success" : "error"}
                      style={{ fontSize: 12 }}
                      className="cp-status-tag"
                    >
                      {orgData.status}
                    </Tag>
                  )}
                  {orgData?.type && (
                    <Tag color="blue" style={{ fontSize: 12 }} className="cp-status-tag">
                      {orgData.type}
                    </Tag>
                  )}
                  {billingStatus === "Draft" && (
                    <Tag color="warning" style={{ fontSize: 12 }} className="cp-status-tag">DRAFT</Tag>
                  )}
                  {billingStatus === "Hold" && (
                    <Tag color="error" style={{ fontSize: 12 }} className="cp-status-tag">ON HOLD</Tag>
                  )}
                </span>
              </BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div style={{ display: "flex", gap: 8 }}>
                {billingStatus === "Draft" && (
                  <Button color="success" onClick={handleActivateCustomer}>
                    <Icon name="check-circle" />
                    <span>Activate</span>
                  </Button>
                )}
                <Link to={`${process.env.PUBLIC_URL}/party-list`}>
                  <Button color="primary" className="btn-dim">
                    <Icon name="arrow-left" />
                    <span>Back to List</span>
                  </Button>
                </Link>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            type="line"
            size="large"
            style={{ background: "#fff", padding: "12px 20px", borderRadius: 4, border: "1px solid #e5e9f2" }}
            tabBarStyle={{ marginBottom: 16 }}
          />
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default PartyDetailsPage;
